import { TranslatePipe } from '../shared/i18n/translate.pipe';
import {
  AfterViewInit,
  Component,
  Inject,
  OnInit,
  PLATFORM_ID
} from '@angular/core';

import {
  CommonModule,
  isPlatformBrowser
} from '@angular/common';

import { FormsModule } from '@angular/forms';

import type * as Leaflet from 'leaflet';

import { HttpClient } from '@angular/common/http';
import { NgForm } from '@angular/forms';
import { inject, signal } from '@angular/core';
import { finalize } from 'rxjs';
import { environment } from '../../environments/environment';

@Component({
  selector: 'app-etudes-bibliques',
  standalone: true,
  imports: [TranslatePipe,
    CommonModule,
    FormsModule
  ],
  templateUrl: './etudes-bibliques.html',
  styleUrls: ['./etudes-bibliques.scss']
})
export class EtudesBibliquesComponent implements AfterViewInit, OnInit {

  private map: any;
  private leaflet: typeof Leaflet | null = null;
  private markers: any[] = [];
  private readonly isBrowser: boolean;

  private readonly http = inject(HttpClient);

  readonly sending = signal(false);
  readonly successMessage = signal('');
  readonly errorMessage = signal('');

  public antennes = [
    {
      name: 'EB Nation',
      lat: 48.8484,
      lng: 2.3959,
      resp: "Danielle, membre de l'Église Saint-Lazare (Paris 8)",
      horaires: 'Vendredi à 20h30',
      type: 'Visioconférence et présentiel'
    },
    {
      name: 'EB Clamart',
      lat: 48.7997,
      lng: 2.2647,
      resp: "Eunice et Sylas, membres de l'Église Protestante Évangélique de Boulogne-Billancourt",
      horaires: 'Jeudi à 20h30',
      type: 'Présentiel'
    },
    {
      name: 'EB Cormeilles-en-Parisis',
      lat: 48.9667,
      lng: 2.2167,
      resp: "Marie-Osée, membre de l'assemblée de Dieu d'Argenteuil",
      horaires: 'Vendredi à 20h30',
      type: 'Présentiel'
    },
    {
      name: 'EB Clichy',
      lat: 48.9038,
      lng: 2.3038,
      resp: "Cadmiel, membre de l'Église Protestante Évangélique des Ternes (Paris 17)",
      horaires: 'Dimanche à 18h00',
      type: 'Présentiel'
    },
    {
      name: 'Antenne Paris Est (77)',
      lat: 48.5432,
      lng: 2.6234,
      resp: "Stanley et Augustin, membres de l'Église Baptiste du Mée-sur-Seine",
      horaires: 'Une rencontre mensuelle le dimanche à partir de 15h',
      type: 'Repas partagé, étude biblique, communion fraternelle et réflexion'
    }
  ];

  public formData = {
    nom: '',
    email: '',
    telephone: '',
    antenne: 'EB Nation',
    message: ''
  };

  constructor(
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngOnInit(): void {
    this.http.get<any[]>(`${environment.apiUrl}/bible-studies`).subscribe({ next: rows => {
      if (!rows.length) return;
      const language = typeof document !== 'undefined' && document.documentElement.lang === 'en' ? 'en' : 'fr';
      this.antennes = rows.map(row => ({ name: row.name, lat: Number(row.latitude), lng: Number(row.longitude),
        resp: row[`responsible_${language}`] || row.responsible_fr,
        horaires: row[`schedule_${language}`] || row.schedule_fr,
        type: row[`details_${language}`] || row.details_fr }));
      this.formData.antenne = this.antennes[0]?.name ?? '';
      if (this.leaflet && this.map) this.renderMarkers(this.leaflet);
    }});
  }

  async ngAfterViewInit(): Promise<void> {

    if (!this.isBrowser) {
      return;
    }

    try {
      const leafletModule = await import('leaflet');

      const L: typeof Leaflet =
        (leafletModule as any).default?.default ??
        (leafletModule as any).default ??
        leafletModule;

      if (typeof (L as any).map !== 'function') {
        console.error('Leaflet map() indisponible', leafletModule);
        return;
      }

      if (typeof (L as any).icon !== 'function') {
        console.error('Leaflet icon() indisponible', leafletModule);
        return;
      }

      this.leaflet = L;
      this.initMap(L);

    } catch (error) {
      console.error('Erreur lors du chargement de Leaflet', error);
    }
  }

  private initMap(L: typeof Leaflet): void {

    const mapContainer = document.getElementById('map');

    if (!mapContainer) {
      console.error('Conteneur #map introuvable');
      return;
    }

    this.map = L.map(mapContainer).setView(
      [48.8566, 2.3522],
      11
    );

    L.tileLayer(
      'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      {
        maxZoom: 19,
        attribution: '&copy; OpenStreetMap contributors'
      }
    ).addTo(this.map);

    const customIcon = L.icon({
      iconUrl: 'carte/marker-icon.png',
      shadowUrl: 'carte/marker-shadow.png',
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowSize: [41, 41]
    });

    this.renderMarkers(L, customIcon);
  }

  private renderMarkers(L: typeof Leaflet, suppliedIcon?: any): void {
    this.markers.forEach(marker => marker.remove());
    this.markers = [];
    const customIcon = suppliedIcon ?? L.icon({ iconUrl: 'carte/marker-icon.png', shadowUrl: 'carte/marker-shadow.png', iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41] });
    this.antennes.forEach(antenne => {

      const marker = L.marker(
        [antenne.lat, antenne.lng],
        {
          icon: customIcon
        }
      ).addTo(this.map);

      marker.bindPopup(`
        <strong>${antenne.name}</strong><br>
        <em>${antenne.horaires}</em><br>
        Responsable : ${antenne.resp}
      `);
      this.markers.push(marker);
    });
  }

 onSubmit(form: NgForm): void {
  if (form.invalid || this.sending()) {
    form.control.markAllAsTouched();
    return;
  }

  this.sending.set(true);
  this.successMessage.set('');
  this.errorMessage.set('');

  const additionalMessage = this.formData.message.trim();

  const payload = {
    type: 'bible_study',
    name: this.formData.nom.trim(),
    email: this.formData.email.trim(),
    phone: this.formData.telephone.trim(),
    subject: this.formData.antenne,
    message: [
      `Demande d'inscription à l'étude biblique : ${this.formData.antenne}`,
      additionalMessage
        ? `Message du demandeur : ${additionalMessage}`
        : 'Aucun message complémentaire.'
    ].join('\n\n'),
    website: ''
  };

  this.http
    .post<{ success: boolean; message?: string }>(
      `${environment.apiUrl}/contact`,
      payload
    )
    .pipe(
      finalize(() => this.sending.set(false))
    )
    .subscribe({
      next: response => {
        this.successMessage.set(
          response.message ??
          `Votre inscription à ${this.formData.antenne} a bien été envoyée. Un accusé de réception vous a été adressé par e-mail.`
        );

        form.resetForm({
          nom: '',
          email: '',
          telephone: '',
          antenne: 'EB Nation',
          message: ''
        });
      },

      error: error => {
        this.errorMessage.set(
          error?.error?.error ??
          `Une erreur est survenue pendant l'envoi de votre inscription. Veuillez réessayer.`
        );
      }
    });
}
}
